import { useMutation } from '@tanstack/react-query';
import { apiPost, apiGet } from '../client';
import { authToken } from '../auth-token';
import type { AuthSuccess, OAuthStartResponse, SignupResponse } from '../types';

export interface EmailVerifyArgs {
  email: string;
  code: string;
}

export function useRequestEmailCode() {
  return useMutation({
    mutationFn: (email: string) =>
      apiPost<{ ttl_seconds: number; resend_after_seconds: number }>(
        '/auth/email/send-code',
        { email, purpose: 'login' },
      ),
  });
}

export function useVerifyEmailCode() {
  return useMutation({
    mutationFn: ({ email, code }: EmailVerifyArgs) =>
      apiPost<AuthSuccess>('/auth/email/verify', { email, code }),
    onSuccess: (data) => {
      authToken.write({
        accessToken: data.access_token,
        accessExpiresAt: data.access_expires_at,
        refreshToken: data.refresh_token,
      });
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: ({ email, password, locale }: { email: string; password: string; locale?: string }) =>
      apiPost<SignupResponse>('/auth/signup', { email, password, locale }),
  });
}

export type OAuthProvider = 'google' | 'facebook';

export function useOAuthStart() {
  return useMutation({
    mutationFn: ({
      provider,
      redirectUri,
    }: {
      provider: OAuthProvider;
      redirectUri: string;
    }) =>
      apiGet<OAuthStartResponse>(
        `/auth/oauth/${provider}/start?redirect_uri=${encodeURIComponent(redirectUri)}`,
      ),
  });
}

export function useOAuthCallback() {
  return useMutation({
    mutationFn: ({
      provider,
      code,
      state,
    }: {
      provider: OAuthProvider;
      code: string;
      state: string;
    }) =>
      apiGet<AuthSuccess>(
        `/auth/oauth/${provider}/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
      ),
    onSuccess: (data) => {
      authToken.write({
        accessToken: data.access_token,
        accessExpiresAt: data.access_expires_at,
        refreshToken: data.refresh_token,
      });
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      try {
        await apiPost<void>('/auth/logout');
      } catch {
        /* even on failure we drop local creds */
      }
      authToken.clear();
    },
  });
}
